<template>
  <button
    class="dn-button"
    :disabled="disable"
    @click="$emit('onClick', $event)"
  >
    <dn-icon
      v-if="icon"
      :name="icon"
      class="icon"
    />
    <slot />
    <span
      v-if="text"
      class="text"
    >{{ text }}</span>
  </button>
</template>

<script lang="ts" setup>
defineEmits(['onClick']);
defineProps({
  text: {
    type: String,
    default: null
  },
  icon: {
    type: String,
    default: null
  },
  disable: {
    type: Boolean,
    default: false
  }
});
</script>

<style lang="scss" scoped>
button {
  --_dn-button_color: var(--dn-button_color, var(--color-link-default, #000));
  --_dn-button_color__hover: var(--dn-button_color__hover, var(--color-link-hover));
  --_dn-button_color__active: var(--dn-button_color__active, var(--color-link-active));

  background: none;
  color: var(--_dn-button_color);
  text-decoration: none;
  cursor: pointer;
  font-family: var(--typography-utility-button-s-font-family);
  letter-spacing: var(--typography-utility-button-s-letter-spacing);
  line-height: var(--typography-utility-button-s-line-height);
  font-size: var(--typography-utility-button-s-font-size);
  transition: all 0.4s cubic-bezier(0, 0.92, 0.32, 0.96);

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed !important;
  }

  .dn-icon {
    margin-right: 6px;
    max-width: 20px;
    width: 20px;
    stroke: var(--color-icon);

    path {
      stroke: var(--color-icon);
    }
  }

  &:hover {
    color: var(--_dn-button_color__hover);

    .dn-icon {
      stroke: var(--color-icon);
    }
  }

  &:active {
    color: var(--_dn-button_color__active);
  }

  &.button,
  &.pill,
  &.outline {
    display: flex;
    align-items: center;
    min-height: 34px;
    letter-spacing: 0.04em;
    white-space: nowrap;
    border-radius: var(--border-radius-button);
    transition: all 200ms ease-in-out;
    inline-size: max-content;
    text-decoration: none;
    cursor: pointer;
    padding: 4px 10px;

    @media only screen and (width >= 768px) {
      padding: 8px 10px;
    }
  }

  &.pill,
  &.outline {
    --_dn-border_color: var(--dn-border_color, var(--color-button-secondary-default-border));
    --_dn-header-button_background: var(--dn-header-button_background, var(--color-button-secondary-default-bg));
    --dn-button_color: var(--dn-header-button_color, var(--color-button-secondary-default-fg));

    background-color: var(--_dn-header-button_background);
    border: 1px solid;
    border-color: var(--_dn-border_color);

    &:focus-visible,
    &:hover {
      --color-icon: var(--color-button-secondary-hover-fg);
      --dn-button_color__hover: var(--dn-header-button_color__hover, var(--color-button-secondary-hover-fg));

      background-color: var(--color-button-secondary-hover-bg);
    }

    &:active {
      --dn-button_color__active: var(--dn-header-button_color__active, var(--color-button-secondary-active-fg));
    }

    &.on-dark {
      --color-button-secondary-default-fg: var(--color-brand-utility-50);
    }

    .icon {
      max-width: 20px;
    }
  }

  .pill {
    padding: 10px 20px;
  }

  &.button {
    background-color: var(--color-button-primary-default-bg);
    color: var(--color-button-primary-default-fg);
    border: 1px solid;
    border-color: var(--color-button-primary-default-border);

    &:focus-visible,
    &:hover {
      --color-icon: var(--color-button-primary-hover-fg);

      background-color: var(--color-button-primary-hover-bg);
      color: var(--color-button-primary-hover-fg);
    }

    &.on-dark {
      --color-button-secondary-default-fg: var(--color-brand-utility-50);
    }

    &-inverted {
      &-primary {
        background-color: var(--color-button-primary-inverted-default-bg);
        color: var(--color-button-primary-inverted-default-fg);
        border-color: var(--color-button-primary-inverted-default-border);

        &:focus-visible,
        &:hover {
          --color-icon: var(--color-button-primary-inverted-hover-fg);

          background-color: var(--color-button-primary-inverted-hover-bg);
          color: var(--color-button-primary-inverted-hover-fg);
        }
      }

      &-secondary {
        background-color: var(--color-button-secondary-inverted-default-bg);
        color: var(--color-button-secondary-inverted-default-fg);
        border-color: var(--color-button-secondary-inverted-default-border);

        &:focus-visible,
        &:hover {
          --color-icon: var(--color-button-secondary-inverted-hover-fg);

          background-color: var(--color-button-secondary-inverted-hover-bg);
          color: var(--color-button-secondary-inverted-hover-fg);
        }
      }
    }

    &-login {
      padding: 0 14px;

      .text {
        padding: 10px 0;
      }

      @media only screen and (width < 768px) {
        display: flex;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-color: var(--color-button-primary-default-border);
        border-radius: 100%;
        padding: 0;

        .dn-icon {
          flex: 0 0 24px;
          max-width: 24px;
          height: 24px;
          stroke: var(--color-neutral-900);
          margin-right: 0;
        }

        .text {
          display: none;
        }
      }

      &-status {
        --dn-border_color: var(--dn-login-status-border_color, var(--color-ui-generic-fg));

        display: flex;
        justify-content: center;
        width: 32px;
        height: 32px;
        min-width: 32px;
        min-height: 32px;
        border-radius: 100%;
        font-size: var(--typography-utility-button-s-font-size);
        padding: 0;

        .dn-icon {
          --color-icon: var(--dn-login-status-icon_color, var(--color-ui-generic-fg));

          flex: 1 0 24px;
          max-width: 24px;
          height: 24px;
          margin-right: 0;
        }

        .text {
          display: block;
        }
      }
    }
  }
}
</style>
